import {
  ICourseComponentPart,
  IUnitInstanceAnalytics,
  IUser,
  IUnitsDataBase,
  IUnitsData,
  IUsersAnalytics,
  UnitChartType,
} from "../../../../../App/Analytics/Common";
import Spinner from "../../../../../components/utils/Spinner";
import Error from "../../../../../components/pages/Error";
import {
  calculateIndividualAssessmentGraphData,
  getUnitAnalytics,
  IIndividualAssessmentAnalytics,
} from "../../../../../parsers/analytics";
import { PreferredLocale } from "../../../../../utils/locales";
import React, { useEffect, useState } from "react";
import LocaleFilterSelection from "../LocaleFilter/LocaleFilterSelection";
import { FilterActionsContainer } from "../../FilterCriteria/FilterCriteria.styles";
import { ExportXLSXIcon } from "../../../../utils/Icons/Analytics/FilterCriteriaIcons";
import { TooltipWrapper } from "../../FilterCriteria/FilterCriteria";
import { Attempts, FilteredAttempts } from "./Attempts";
import { UnitInstanceChart } from "./UnitInstanceAnalytics";
import { SectionContainer } from "../../common/Charts.styles";
import { objectMap } from "../../../../../utils";
import { IUnitInstanceAnalyticsGraphData } from "../../../../../formatters/analytics";
import { connect } from "react-redux";
import { Dispatch } from "redux";
import { Creators as AllCreators } from "../../../../../actions";
import { IAction } from "reduxsauce";
import { IState } from "../../../../../App/reducer";
import styled from "styled-components";
import { ICourseLocalizedText, IFullCourse } from "../../../../../components/course-author/types";
import * as selectors from "../../../../../selectors/selectors";
import { api } from "../../../../../dependencies";
const Creators = (AllCreators as any).App.Users;
import AnalyticsWorker from "worker-loader!./worker/analytics.worker";

export interface IIndividualAssessmentAnalyticsOwnProps {
  analytics: IUnitInstanceAnalytics;
  courseAnalytics: IUsersAnalytics;
  courseDisplayName: string;
  courseId: string;
  individualAnswers: ICourseComponentPart | undefined;
  locales: PreferredLocale[];
  masterLocale: PreferredLocale;
  objectDisplayName: string;
  onSelectLocale: (locale: React.ChangeEvent<HTMLSelectElement>) => void;
  selectedLocale: PreferredLocale | undefined;
  selectedUnitLocalizedName: ICourseLocalizedText[];
  selectedUsers: Set<string>;
  users: IUser[];
}

interface IIndividualAssessmentAnalyticsState {
  loading: boolean;
  individualAnalytics: IIndividualAssessmentAnalytics[];
}

interface IIndividualAssessmentAnalyticsStateProps {
  accountId: string;
  organizationName: string;
}

interface IIndividualAssessmentAnalyticsDispatchProps {
  exportAttemptsAnalytics: (
    attemptsAnalytics: Record<string, IUnitsDataBase> | undefined,
    selectedUnitName: string
  ) => void;
}

type IIndividualAssessmentAnalyticsProps = IIndividualAssessmentAnalyticsOwnProps &
  IIndividualAssessmentAnalyticsStateProps &
  IIndividualAssessmentAnalyticsDispatchProps;

const AttemptsHeaderContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: flex-end;
  padding-right: 1rem;
  padding-bottom: 0.5rem;
`;

const AttemptsExportIconsContainer = styled(FilterActionsContainer)`
  gap: 1vw;
`;

const IndividualAssessmentAnalytics: React.SFC<IIndividualAssessmentAnalyticsProps> = ({
  analytics,
  courseAnalytics,
  courseId,
  individualAnswers,
  locales,
  masterLocale,
  onSelectLocale,
  selectedLocale,
  selectedUnitLocalizedName,
  selectedUsers,
  users,
  exportAttemptsAnalytics,
  accountId,
  objectDisplayName,
  organizationName,
}) => {
  const [exportInProgress, setExportInProgress] = React.useState<boolean>(false);
  const [exportError, setExportError] = React.useState<string | undefined>(undefined);
  const [individualAnalytics, setIndividualAnalytics] = useState<IIndividualAssessmentAnalyticsState>({
    individualAnalytics: [{}],
    loading: true,
  });
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    const resolveDataPromise = new Promise(res => {
      const info = calculateIndividualAssessmentGraphData(
        users,
        analytics,
        courseAnalytics,
        selectedUsers,
        locales,
        selectedLocale,
        masterLocale
      );
      return res(info);
    });

    resolveDataPromise.then(d => {
      setIndividualAnalytics(d as IIndividualAssessmentAnalyticsState);
      setLoading(false);
    });
  }, [users, analytics, courseAnalytics, selectedUsers, locales, masterLocale]);

  if (loading) {
    return <Spinner />;
  }

  if (individualAnswers === undefined) {
    return <Error />;
  }

  const dataLanguagesObject = objectMap(individualAnalytics[0], (elem: IUnitInstanceAnalyticsGraphData) =>
    getUnitAnalytics(elem, elem.name ?? "")
  );
  const filteredDataLanguagesObject = objectMap(individualAnalytics[1], (elem: IUnitInstanceAnalyticsGraphData) =>
    getUnitAnalytics(elem, elem.name ?? "")
  );

  const dataLanguages: IUnitsData[] = Object.values(dataLanguagesObject);
  const filteredDataLanguages: IUnitsData[] = Object.values(filteredDataLanguagesObject);

  const isLocaleFilterActive = selectedLocale !== undefined;
  const isUserFilterActive = selectedUsers.size > 0;
  const isFilterActive = isLocaleFilterActive || isUserFilterActive;
  const selectedDataCondition = isLocaleFilterActive && isUserFilterActive ? selectedLocale : "total";
  const filteredDataCondition = isLocaleFilterActive ? selectedLocale : "total";

  const selectedDataByLanguage = dataLanguages.find(elem => elem.locale === selectedDataCondition);
  const filteredDataByLanguage = filteredDataLanguages.find(elem => elem.locale === filteredDataCondition);

  const emptyData = {
    Correct: { amount: 0, percentage: 0 },
    "Didn't Know": { amount: 0, percentage: 0 },
    Incorrect: { amount: 0, percentage: 0 },
    locale: PreferredLocale.en,
    name: "",
  } as IUnitsData;

  const selectedUnitName =
    selectedUnitLocalizedName.find(u => u.locale === masterLocale)?.value.text ?? "Untitled Unit";

  const handleExportAttempts = () =>
    exportAttemptsAnalytics(selectedDataByLanguage?.answersByQuestion, selectedUnitName);

  const handleExportLearners = () => {
    setExportInProgress(true);
    const coursePromise = api.resolve.courseFull.post(accountId, [courseId]);

    coursePromise.then(([err, res]) => {
      if (err || !res) {
        return setExportError("Failed to retrieve course information.");
      }

      const course: IFullCourse = res?.data[0]?.value;
      const action = {
        content: individualAnswers,
        course,
        courseId,
        masterLocale,
        objectDisplayName,
        organizationName,
        selectedUnitLocalizedName,
        unitInstanceAnalytics: analytics,
        users,
        usersCourseAnalytics: courseAnalytics,
      };
      const workerInstance = new AnalyticsWorker();
      workerInstance.postMessage(JSON.stringify(action));
      workerInstance.onmessage = (event: MessageEvent) => {
        /* this is the standard HTML5 download attribute technique */
        const a = document.createElement("a");
        a.download = "export-learners.xlsx";
        a.href = event.data.url;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        setExportInProgress(false);
      };
    });
  };

  return (
    <SectionContainer data-testid="individual-assessment-analytics">
      {exportError && alert(exportError)}
      {dataLanguages.map((dataPoint, idx: number) => (
        <UnitInstanceChart
          key={dataPoint.name}
          data={dataPoint}
          filteredData={isUserFilterActive ? filteredDataLanguages[idx] : emptyData}
          onSelectAssessmentName={undefined}
          unitInstanceAnalytics={undefined}
          isLast={idx === dataLanguages.length - 1}
          isFilterActive={isUserFilterActive}
        />
      ))}
      <AttemptsHeaderContainer>
        <div>
          <LocaleFilterSelection
            supportedLocales={locales}
            onSelectLocale={onSelectLocale}
            selectedLocale={selectedLocale}
            localeFilterType={UnitChartType.Instruction}
          />
        </div>
        <AttemptsExportIconsContainer data-testid="attempts-export-icons-container">
          <div data-testid="export-attempts-container">
            <TooltipWrapper id="export-attempts" content="Export answers">
              <ExportXLSXIcon onClick={handleExportAttempts} disabled={false} />
            </TooltipWrapper>
          </div>
          <div data-testid="export-learners-container">
            <TooltipWrapper id="export-learners" content="Export learners">
              <ExportXLSXIcon onClick={handleExportLearners} disabled={exportInProgress} />
            </TooltipWrapper>
          </div>
        </AttemptsExportIconsContainer>
      </AttemptsHeaderContainer>
      {isFilterActive ? (
        <FilteredAttempts
          content={individualAnswers}
          filteredGraphData={filteredDataByLanguage?.answersByQuestion}
          graphData={selectedDataByLanguage?.answersByQuestion}
          currentLocale={masterLocale}
        />
      ) : (
        <Attempts
          content={individualAnswers}
          graphData={selectedDataByLanguage?.answersByQuestion}
          currentLocale={masterLocale}
        />
      )}
    </SectionContainer>
  );
};

const mapStateToProps = (state: IState): IIndividualAssessmentAnalyticsStateProps => ({
  accountId: selectors.accountId(state),
  organizationName: selectors.organizationName(state),
});

const mapDispatchToProps = (
  dispatch: Dispatch<IAction>,
  ownProps: IIndividualAssessmentAnalyticsOwnProps
): IIndividualAssessmentAnalyticsDispatchProps => ({
  exportAttemptsAnalytics: (attemptsAnalytics: Record<string, IUnitsDataBase> | undefined, selectedUnitName: string) =>
    dispatch(
      Creators.exportAttemptsAnalytics(
        ownProps.courseDisplayName,
        ownProps.objectDisplayName,
        selectedUnitName,
        ownProps.individualAnswers,
        attemptsAnalytics,
        ownProps.masterLocale
      )
    ),
});

export default connect<
  IIndividualAssessmentAnalyticsStateProps,
  IIndividualAssessmentAnalyticsDispatchProps,
  IIndividualAssessmentAnalyticsOwnProps,
  {}
>(
  mapStateToProps,
  mapDispatchToProps
)(IndividualAssessmentAnalytics);
